import { loadFromStorage, saveToStorage } from "@/lib/storage";
import { USERS, LOCAL_STORAGE_KEYS, ALL_DAYS_KEYS, INITIAL_DAYS_DAVID, INITIAL_DAYS_SARA } from "@/config/constants";
import { formatDateKey, addWeeksToDateKey, parseDateKey, getDayNameFromDateKey, getWeekDays } from '@/lib/dateUtils';
import { getInitialDayDataForUser } from './dogDayManagerActions';
import { saveTemplate, getTemplate } from '@/lib/templateSync';

import { saveDogs } from '@/realtimeDogs';
import { generateDogId, normalizeDogName, findTemplateMatchForImportKey } from '@/lib/dogIdentity';
import { resolveNoteForDate } from '@/lib/noteRules';


const deepClone = (obj) => JSON.parse(JSON.stringify(obj));
const MAX_WEEKS_AHEAD_TO_PROPAGATE_FIXED_DOGS = 26; // 6 meses para perros fijos

const parseDogDataFromText = (text) => {
    console.log("[PDF_PARSER] Raw text from PDF:", text);
    
    const dayMap = {
        'LUNES': 'LUNES', 'MARTES': 'MARTES', 'MIÉRCOLES': 'MIÉRCOLES', 'MIERCOLES': 'MIÉRCOLES', 
        'JUEVES': 'JUEVES', 'VIERNES': 'VIERNES', 'SÁBADO': 'SÁBADO', 'SABADO': 'SÁBADO',
        'DOMINGO': 'DOMINGO'
    };

    // Procesar línea por línea con índice para poder saltar líneas de URLs
    const lines = text.split('\n').filter(line => line.trim().length > 0);
    const parsedDogs = [];

    console.log(`[PDF_PARSER] Processing ${lines.length} lines`);

    for (let i = 0; i < lines.length; i++) {
        const rawLine = lines[i].trim();
        
        // Dividir por ';' y limpiar espacios
        const parts = rawLine.split(';').map(p => p.trim()).filter(p => p.length > 0);
        
        console.log(`[PDF_PARSER] Line ${i}:`, rawLine);
        console.log(`[PDF_PARSER] Parts (${parts.length}):`, parts);

        // Requerir mínimo 2 partes (nombre y días)
        if (parts.length < 2) {
            console.warn(`[PDF_PARSER] Line ${i} ignored: menos de 2 campos requeridos (nombre y días)`);
            continue;
        }

        // Asignar partes
        const [rawName, rawDays, rawWhatsApp, rawMaps] = parts;
        
        const name = rawName?.toUpperCase();
        if (!name) {
            console.warn(`[PDF_PARSER] Line ${i} ignored: nombre vacío`);
            continue;
        }

        // Parsear días
        const daysText = rawDays?.toUpperCase() || '';
        const days = daysText.split(',').map(d => {
            const trimmed = d.trim();
            const mapped = dayMap[trimmed];
            if (!mapped) {
                console.warn(`[PDF_PARSER] Día desconocido: "${trimmed}"`);
            }
            return mapped;
        }).filter(Boolean);

        if (days.length === 0) {
            console.warn(`[PDF_PARSER] Line ${i} ignored: ningún día válido`);
            continue;
        }

        // WhatsApp: aceptar enlaces válidos o teléfono con prefijo internacional
        let whatsappGroup = null;

if (rawWhatsApp) {
    const cleanWhatsApp = rawWhatsApp.trim();

    if (
        cleanWhatsApp.startsWith('https://wa.me/') ||
        cleanWhatsApp.startsWith('https://chat.whatsapp.com/') ||
        cleanWhatsApp.startsWith('https://api.whatsapp.com/')
    ) {
        whatsappGroup = cleanWhatsApp;
    } else {
        const phoneOnly = cleanWhatsApp
            .replace(/[\s\-()]/g, '')
            .replace(/^\+/, '');

        if (/^\d{10,15}$/.test(phoneOnly)) {
            whatsappGroup = `https://wa.me/${phoneOnly}`;
        }
    }
}
        // Determinar locationUrl
        let locationUrl = null;
        
        // Caso 1: maps en la misma línea (4º campo)
        if (rawMaps && rawMaps.startsWith('http')) {
            locationUrl = rawMaps;
        } else {
            // Caso 2: maps en la siguiente línea
            const nextLine = (lines[i + 1] || '').trim();
            if (nextLine && (nextLine.startsWith('http://') || nextLine.startsWith('https://'))) {
                locationUrl = nextLine;
                // Muy importante: saltar la siguiente línea para no crear un perro fantasma
                i++;
                console.log(`[PDF_PARSER] Maps URL found in next line, skipping it (i is now ${i})`);
            }
        }

        console.log(`[PDF_PARSER] Parsed - Name: ${name}, Days: ${days.join(', ')}, WhatsApp: ${whatsappGroup ? '✓' : '✗'}, Maps: ${locationUrl ? '✓' : '✗'}`);

        const dog = { 
            name, 
            days, 
            whatsappGroup, 
            locationUrl,
            isFixed: true, 
            count: 1 
        };

        console.log(`[PDF_PARSER] Dog parsed:`, dog);
        parsedDogs.push(dog);
    }

    console.log(`[PDF_PARSER] Total dogs parsed: ${parsedDogs.length}`);
    return parsedDogs;
};

 export const handleBulkUpdateFromPDF = async (
     pdfText,
     daysList,
     setDaysList,
     recordSnapshot,
     toast,
     selectedUser,
     handleAddDogToListFunc,
     selectedDayKey,
     currentDate,
     markDirty,
     canUseUnlimitedClients = true,
     workspaceId
 ) => {
    try {
        console.log("[PDF_BUILD] Starting PDF import process", {
            currentDaysListKeys: Object.keys(daysList || {}).slice(0, 5),
            currentDaysListLength: Object.keys(daysList || {}).length
        });

         const parsedDogData = parseDogDataFromText(pdfText);
         if (parsedDogData.length === 0) {
             console.warn("[PDF_HANDLER] No valid dog data found in PDF");
             toast({ title: "Sin datos", description: "No se encontraron datos de perros válidos en el PDF.", variant: "destructive" });
             return;
         }

         // ── Gate Free: máximo 3 clientes por día ──
         if (!canUseUnlimitedClients) {
             const dogCountByDay = {};
             parsedDogData.forEach(dog => {
                 dog.days.forEach(dayName => {
                     dogCountByDay[dayName] = (dogCountByDay[dayName] || 0) + 1;
                 });
             });
             const maxDayCount = Math.max(0, ...Object.values(dogCountByDay));
             if (maxDayCount > 3) {
                 return false; // AppCore gestiona el toast y el modal
             }
         }

         console.log("[PDF_BUILD] ✓ Dogs parsed from PDF:", parsedDogData.length, {
             dogs: parsedDogData.map(d => ({ name: d.name, days: d.days }))
         });

        // Fase 2 — REGLA CRÍTICA: antes de construir newDaysList, leer el
        // template del perfil y resolver, para cada línea del PDF, si su
        // importKey (nombre normalizado) ya existe en templateDogs. Si existe,
        // reutilizar su id/name/originalOrder (evita recrear clientes antiguos
        // o duplicarlos). Si no existe, generar id nuevo con importKey=name.
        //
        // 🔒 PROTECCIÓN ANTI-CACHÉ: esta lectura debe ser OBLIGATORIAMENTE
        // contra el servidor (source: "server", throwOnError: true). Una
        // plantilla cacheada localmente en Firestore puede resucitar nombres
        // editados antiguos aunque el documento ya no exista en el servidor
        // (p.ej. borrado manual desde Firebase Console). Si la lectura
        // estricta falla (red/permisos), se aborta TODA la importación antes
        // de construir newDaysList, generar IDs o escribir perros — nunca se
        // hace fallback a una copia cacheada.

        let existingTemplateDogs = [];
        if (workspaceId) {
            try {
                const existingTemplate = await getTemplate(
                    workspaceId,
                    selectedUser,
                    { source: "server", throwOnError: true }
                );
                existingTemplateDogs = Array.isArray(existingTemplate?.templateDogs)
                    ? existingTemplate.templateDogs
                    : [];
            } catch (e) {
                console.error('[PDF_TEMPLATE_READ_ERR]', e?.message);
                toast({
                    title: "Error",
                    description: "No se pudieron actualizar los datos desde el PDF.",
                    variant: "destructive"
                });
                return; // abortar import: nunca continuar con plantilla cacheada
            }
        }

        // Resolución importKey → identidad (id/name/originalOrder existentes).
        // Se construye UNA vez y se usa para todas las líneas del PDF.
        const identityByImportKey = new Map();
        parsedDogData.forEach(dog => {
            const importKey = normalizeDogName(dog.name);
            if (identityByImportKey.has(importKey)) return; // ya resuelto
            const match = findTemplateMatchForImportKey(existingTemplateDogs, importKey);
            if (match) {
                // Cliente ya conocido: reutilizar id, name visible editado y
                // originalOrder manual si existe. NUNCA generar id nuevo aquí.
                identityByImportKey.set(importKey, {
                    id: match.id || generateDogId(), // backfill legacy sin id
                    importKey,
                    name: match.name || dog.name.toUpperCase(),
                    originalOrder: match.originalOrder,
                    // Conservar histórico de notas recurrentes: sin esto, un
                    // reimport de PDF sobre un cliente ya existente descartaba
                    // silenciosamente su recurringNotes (quedaba []), dejando
                    // "huecos" en semanas futuras aún no materializadas, ya
                    // que templateDogsMap/dogObj más abajo se construyen a
                    // partir de esta identidad.
                    recurringNotes: Array.isArray(match.recurringNotes) ? match.recurringNotes : [],
                });
            } else {
                // Cliente nuevo: generar id, importKey y name inicial = nombre PDF
                identityByImportKey.set(importKey, {
                    id: generateDogId(),
                    importKey,
                    name: dog.name.toUpperCase(),
                    originalOrder: undefined,
                    recurringNotes: [],
                });
            }
        });

        console.error('[PDF_IDENTITY_RESOLVE]', JSON.stringify({
            profile: selectedUser,
            totalDogs: parsedDogData.length,
            existingTemplateDogsLen: existingTemplateDogs.length,
            matched: Array.from(identityByImportKey.values()).filter(v =>
                existingTemplateDogs.some(t => t.id === v.id)
            ).length,
        }));

        // 1️⃣ OBTENER DATEKEYS DE LA SEMANA ACTUAL (LUNES-VIERNES)

        const weekDays = getWeekDays(currentDate || new Date());
        const weekDayKeys = weekDays.slice(0, 5).map(date => formatDateKey(date)); // lunes-viernes (índices 0-4)

        console.log("[PDF_BUILD] Week dayKeys (lunes-viernes):", weekDayKeys);

        const dayNameToIndex = {
            LUNES: 0,
            MARTES: 1,
            MIÉRCOLES: 2,
            MIERCOLES: 2,
            JUEVES: 3,
            VIERNES: 4,
            SÁBADO: 5,
            SABADO: 5,
            DOMINGO: 6
        };

        // 2️⃣ CONSTRUIR newDaysList DESDE CERO CON SOLO DAYKEYS "YYYY-MM-DD"
        // 🔥 CRÍTICO: NO copies Object.keys(daysList) que pueden ser composite keys "profile::YYYY-MM-DD"
        // Crear SOLO con dayKeys simples (YYYY-MM-DD) para evitar contaminación
        const newDaysList = {};
        
        // Inicializar SOLO los 5 días de la semana con keys simples
        weekDayKeys.forEach(dayKey => {
            newDaysList[dayKey] = [];
        });

        console.log("[PDF_BUILD] newDaysList structure initialized:", {
            totalKeys: Object.keys(newDaysList).length,
            weekDayKeys: weekDayKeys,
            allKeys: Object.keys(newDaysList)
        });

        // 3️⃣ ITERAR PERROS, DISTRIBUIR Y PROPAGAR A FUTURAS SEMANAS
        // 🔥 TRACK: Registrar todos los dayKeys que se tocan (para marcar dirty solo estos)
        const touchedDayKeys = new Set();

        parsedDogData.forEach((dog, dogIndex) => {
            console.log(`[PDF_BUILD] Processing dog ${dogIndex + 1}/${parsedDogData.length}:`, {
                name: dog.name,
                daysFromPDF: dog.days,
                isFixed: dog.isFixed
            });

            // Convertir nombres de días a sus dateKeys
            dog.days.forEach(dayName => {
                const dayIndex = dayNameToIndex[dayName];
                if (dayIndex === undefined) {
                    console.warn(`[PDF_BUILD] Unknown day name: ${dayName}`);
                    return;
                }

                // Si el índice de día es 0-4 (lunes-viernes), obtener el dayKey
                if (dayIndex >= 0 && dayIndex <= 4) {
                    const baseDayKey = weekDayKeys[dayIndex];
                    const dayName = getDayNameFromDateKey(baseDayKey);

                    // Identidad resuelta para este cliente (id/importKey/name/originalOrder)
                    const importKey = normalizeDogName(dog.name);
                    const identity = identityByImportKey.get(importKey);

                    console.log(`[PDF_BUILD] Assigning "${identity?.name || dog.name}" to ${dayName} (${baseDayKey})`);

                    // Crear objeto de perro normalizado, con identidad estable
                    // (id/importKey reutilizados si el cliente ya existía, name
                    // visible = el editado si existía, o el del PDF si es nuevo).
                    // recurringNotes se conserva desde la identidad resuelta
                    // (match de plantilla existente, o [] si el cliente es
                    // nuevo). informativeNote/informativeNoteOverride NO se
                    // fijan aquí como constante: se recalculan por fecha
                    // exacta (baseDayKey o cada futureWeekDayKey) más abajo,
                    // porque un histórico con endDateKey puede aplicar a
                    // unas semanas sí y a otras no dentro de las 26 propagadas.
                    const dogObj = {
                        id: identity?.id,
                        importKey: identity?.importKey,
                        name: identity?.name || dog.name.toUpperCase(),
                        whatsappGroup: dog.whatsappGroup || null,
                        locationUrl: dog.locationUrl || null,
                        count: dog.count || 1,
                        isFixed: dog.isFixed || true,
                        originalOrder: identity?.originalOrder ?? (Date.now() + Math.random()),
                        firstAddedDateKey: baseDayKey,
                        recurringNotes: identity?.recurringNotes || [],
                    };

                    // 🔴 SI ES FIJO: Propagar a 26 semanas futuras
                    if (dogObj.isFixed) {
                        console.log(`[PDF_BUILD] Perro fijo: propagando "${dogObj.name}" a ${MAX_WEEKS_AHEAD_TO_PROPAGATE_FIXED_DOGS} semanas`);
                        
                        // Añadir a la semana actual y a todas las futuras
                        for (let weekOffset = 0; weekOffset < MAX_WEEKS_AHEAD_TO_PROPAGATE_FIXED_DOGS; weekOffset++) {
                            const futureWeekDayKey = addWeeksToDateKey(baseDayKey, weekOffset);
                            
                            // Crear estructura si no existe
                            if (!(futureWeekDayKey in newDaysList)) {
                                newDaysList[futureWeekDayKey] = [];
                            }

                            // Añadir perro si no existe ya — deduplicar por identidad
                            // estable (id) para no duplicar aunque el nombre visible
                            // haya cambiado tras un renombrado manual.
                            const alreadyPresent = newDaysList[futureWeekDayKey].find(
                                d => (dogObj.id && d.id === dogObj.id) || d.name === dogObj.name
                            );
                            if (!alreadyPresent) {
                                newDaysList[futureWeekDayKey].push({
                                    ...dogObj,
                                    originalOrder: identity?.originalOrder ?? (Date.now() + Math.random()),
                                    // Resolver nota para ESTA fecha concreta (no
                                    // reutilizar un único valor para las 26 semanas).
                                    informativeNote: resolveNoteForDate(dogObj.recurringNotes, futureWeekDayKey),
                                    informativeNoteOverride: false,
                                });
                                // 🔥 TRACK: Marcar que este dayKey fue tocado
                                touchedDayKeys.add(futureWeekDayKey);
                            }
                        }
                        console.log(`[PDF_BUILD] ✓ Propagated fixed dog "${dogObj.name}" to ${MAX_WEEKS_AHEAD_TO_PROPAGATE_FIXED_DOGS} weeks`);
                    } else {
                        // 🔵 NO FIJO: Solo añadir a la semana actual
                        const alreadyPresent = newDaysList[baseDayKey].find(
                            d => (dogObj.id && d.id === dogObj.id) || d.name === dogObj.name
                        );
                        if (!alreadyPresent) {
                            newDaysList[baseDayKey].push({
                                ...dogObj,
                                informativeNote: resolveNoteForDate(dogObj.recurringNotes, baseDayKey),
                                informativeNoteOverride: false,
                            });
                            // 🔥 TRACK: Marcar que este dayKey fue tocado
                            touchedDayKeys.add(baseDayKey);
                            console.log(`[PDF_BUILD] ✓ Added non-fixed dog "${dogObj.name}" to ${baseDayKey}`);
                        } else {
                            console.log(`[PDF_BUILD] ⚠️ Skipping duplicate "${dogObj.name}" in ${baseDayKey}`);
                        }
                    }
                }

            });
        });

        // Ordenar perros en cada día
        Object.keys(newDaysList).forEach(dayKey => {
            if (Array.isArray(newDaysList[dayKey])) {
                newDaysList[dayKey].sort((a, b) => (a.originalOrder || 0) - (b.originalOrder || 0));
            }
        });

        // 4️⃣ LOG FINAL STATE BEFORE setDaysList
        console.log("[PDF_BUILD] Final newDaysList structure:", {
            totalKeys: Object.keys(newDaysList).length,
            weekDayKeys: weekDayKeys,
            content: weekDayKeys.map(dayKey => ({
                dayKey: dayKey,
                dogsCount: (newDaysList[dayKey] || []).length,
                dogs: (newDaysList[dayKey] || []).map(d => d.name)
            }))
        });

        // 5️⃣ HACER UN ÚNICO setDaysList
        console.log("[PDF_BUILD] Setting daysList with all dogs distributed...");
        setDaysList(newDaysList);

        // ✅ PERSISTENCIA WRITE-THROUGH DIRECTA: No esperar autosave (300ms)
        // 🔥 CRÍTICO: for...of + await para guardar cada dayKey secuencialmente

        
        console.error("[PDF_PERSIST_START]", JSON.stringify({
          profile: selectedUser,
          workspaceId: workspaceId || null,
          touchedDayKeysCount: touchedDayKeys.size,
          timestamp: new Date().toISOString()
        }));
        
        if (!workspaceId) {
          console.error("[PDF_PERSIST_ERROR]", JSON.stringify({
            reason: "no-workspaceId",
            profile: selectedUser,
            timestamp: new Date().toISOString()
          }));
        } else if (touchedDayKeys.size > 0) {
          // ✅ for...of + await DIRECTO (secuencial, garantizado completar antes de regresar)
          const ordered = Array.from(touchedDayKeys).sort();
          let successCount = 0;
          let failCount = 0;
          
          for (const dayKey of ordered) {
            // 🔥 SANITY CHECK: dayKey NUNCA debe contener '::'
            if (dayKey.includes('::')) {
              console.error("[PDF_PERSIST_SANITY_FAIL]", JSON.stringify({
                dayKey,
                reason: "dayKey-contains-composite-separator",
                profile: selectedUser,
                willSkip: true
              }));
              failCount++;
              continue;
            }
            
            const dogsForDay = newDaysList[dayKey] || [];
            if (dogsForDay.length === 0) {
              console.log("[PDF_PERSIST_SKIP]", JSON.stringify({
                dayKey,
                reason: "no-dogs-for-day"
              }));
              continue;
            }
            
            try {
              console.error("[PDF_PERSIST] start", JSON.stringify({
                dayKey,
                profile: selectedUser,
                dogsLen: dogsForDay.length,
                path: `workspaces/${workspaceId}/days/${dayKey}/profiles/${selectedUser}`,
                phase: "before-save",
                timestamp: new Date().toISOString()
              }));
              
              // Llamar saveDogs con dayKey SIMPLE (nunca composite)
              await saveDogs(workspaceId, dayKey, dogsForDay, selectedUser);
              
              console.error("[PDF_PERSIST] ok", JSON.stringify({
                dayKey,
                profile: selectedUser,
                dogsLen: dogsForDay.length,
                path: `workspaces/${workspaceId}/days/${dayKey}/profiles/${selectedUser}`,
                phase: "after-save",
                timestamp: new Date().toISOString()
              }));
              
              successCount++;
            } catch (err) {
              console.error("[PDF_PERSIST_ERROR]", JSON.stringify({
                dayKey,
                profile: selectedUser,
                error: err?.message || String(err),
                phase: "saveDogs-failed",
                timestamp: new Date().toISOString()
              }));
              failCount++;
            }
          }
          
          console.error("[PDF_PERSIST_DONE]", JSON.stringify({
            profile: selectedUser,
            workspaceId,
            successCount,
            failCount,
            totalTouched: touchedDayKeys.size,
            timestamp: new Date().toISOString()
          }));
        }

        // ✅ MARCAR DIRTY: Solo los dayKeys que realmente fueron tocados con datos (DIFF-BASED)
        // 🔥 CRÍTICO: Usar touchedDayKeys que se construyó explícitamente, NO Object.keys(newDaysList)
        // porque newDaysList puede tener composite keys y placeholders vacíos
        if (markDirty && touchedDayKeys.size > 0) {
          touchedDayKeys.forEach(dayKey => {
            // Verificar que el dayKey tiene datos antes de marcar dirty
            const arr = Array.isArray(newDaysList[dayKey]) ? newDaysList[dayKey] : [];
            const willMarkDirty = arr.length > 0;
            
            if (willMarkDirty) {
              markDirty(selectedUser, dayKey);
              console.error('[PDF_DIRTY]', JSON.stringify({
                dayKey,
                compositeKey: `${selectedUser}::${dayKey}`,
                profile: selectedUser,
                dogsLen: arr.length,
                willMarkDirty: true,
                reason: 'PDF_UPLOAD_WITH_DOGS',
                timestamp: new Date().toISOString()
              }));
            } else {
              // Placeholder vacío: NO marcar dirty (evita escrituras masivas dogsLen:0)
              console.error('[PDF_DIRTY]', JSON.stringify({
                dayKey,
                compositeKey: `${selectedUser}::${dayKey}`,
                profile: selectedUser,
                dogsLen: 0,
                willMarkDirty: false,
                reason: 'PDF_PLACEHOLDER_EMPTY_SKIP',
                timestamp: new Date().toISOString()
              }));
            }
          });
        }

        // Marcar todos los perros como eliminados permanentemente para que no se recarguen
        saveToStorage(LOCAL_STORAGE_KEYS.PERMANENTLY_DELETED_DOGS, [], selectedUser);

        console.log("[PDF_BUILD] ✅ PDF import completed successfully");

        // 6️⃣ GUARDAR PLANTILLA: Extraer lista normalizada de perros únicos del PDF
        // 🔥 workspaceId ya fue obtenido para persistencia directa, reutilizarlo
        // Fase 2: guardar id, importKey y name (el editado si el cliente ya
        // existía, o el del PDF si es nuevo) — NUNCA solo por nombre, para no
        // perder la identidad estable ni resucitar duplicados en el futuro.
        if (workspaceId) {
            // Construir lista de templateDogs (sin duplicados, con estructura completa)
            const templateDogsMap = new Map();
            parsedDogData.forEach(dog => {
                const importKey = normalizeDogName(dog.name);
                const identity = identityByImportKey.get(importKey);
                // recurringNotes: propagar el histórico ya resuelto en
                // identityByImportKey (match de plantilla existente, o []
                // para clientes nuevos). Sin este campo, cada reimport de
                // PDF sobrescribía templates/{profile}.templateDogs con
                // recurringNotes ausente, perdiendo la regla recurrente de
                // origen para toda futura extendTemplateWindow.
                const dogObj = {
                    id: identity?.id,
                    importKey: identity?.importKey || importKey,
                    name: identity?.name || dog.name.toUpperCase(),
                    whatsappGroup: dog.whatsappGroup || null,
                    locationUrl: dog.locationUrl || null,
                    count: dog.count || 1,
                    isFixed: true,
                    originalOrder: identity?.originalOrder ?? (Date.now() + Math.random()),
                    firstAddedDateKey: weekDayKeys[0], // Primera fecha de la semana
                    recurringNotes: identity?.recurringNotes || [],
                };
                // Deduplicar por identidad (id) en lugar de por nombre visible
                templateDogsMap.set(dogObj.id || dogObj.name, dogObj);
            });
            
            const templateDogs = Array.from(templateDogsMap.values());
            const windowEndDayKey = addWeeksToDateKey(weekDayKeys[0], MAX_WEEKS_AHEAD_TO_PROPAGATE_FIXED_DOGS);
            
            // Guardar template en Firestore (async, no bloqueante)
            saveTemplate(workspaceId, selectedUser, templateDogs, windowEndDayKey)
                .then(() => {
                    console.log('[PDF_BUILD] ✅ Template saved successfully');
                })
                .catch(err => {
                    console.error('[PDF_BUILD] ❌ Error saving template:', err);
                });
        }


        toast({ 
            title: "Datos actualizados", 
            description: `${parsedDogData.length} perro${parsedDogData.length !== 1 ? 's' : ''} ${parsedDogData.length !== 1 ? 'han' : 'ha'} sido cargado${parsedDogData.length !== 1 ? 's' : ''} desde el PDF en lunes–viernes.` 
        });
    } catch (error) {
        console.error("Error en bulk update:", error);
        toast({ title: "Error", description: "No se pudieron actualizar los datos desde el PDF.", variant: "destructive" });
    }
};
